import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { ForumPost, ApiForumPost } from '../../models/ForumPost';
import { Donor, ApiDonor } from '../../models/Donor';
import { useAuthUserContext } from '../../providers';
import { UserType } from '../../types';
import { useTitle } from '../../hooks/useTitle';
import { useMetaDescription } from '../../hooks/useMetaDescription';
import { buildForumPostTitle, buildForumPostDescription } from './buildForumPostMeta';
import { AGE_RANGE_TEXTS, BODY_FIGURE_TEXTS, HEIGHT_RANGE_TEXTS } from '../../consts';
import Header from '../forum/Header';
import Footer from '../../components/Footer';
import Spinner from '../../components/Spinner';
import { toast } from 'react-toastify';
import './index.scss';

function formatDate(isoStr: string): string {
  const d = new Date(isoStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${ y }/${ m }/${ day }`;
}

const ForumPostDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [ post, setPost ] = useState<ForumPost>();
  const [ donor, setDonor ] = useState<Donor>();
  const [ showModal, setShowModal ] = useState(false);
  const [ comment, setComment ] = useState('');
  const [ applied, setApplied ] = useState(false);
  const [ applicationsCount, setApplicationsCount ] = useState(0);
  const { get, post: apiPost, loading } = useApi();
  const authUser = useAuthUserContext().user;

  useTitle(post ? buildForumPostTitle(post) : 'Ton Bébé | 募集詳細');
  useMetaDescription(post ? buildForumPostDescription(post) : undefined);

  useEffect(() => {
    get({
      endpoint: `api/v1/forum_posts/${ id }`,
      skipAuth: true,
      onSuccess: (res) => {
        const loaded = new ForumPost(res.data as ApiForumPost);
        setPost(loaded);
        setApplied(loaded.applied);
        setApplicationsCount(loaded.applicationsCount);
      },
    });
  }, [ get, id ]);

  useEffect(() => {
    if (authUser?.userType === UserType.Donor) {
      get({
        endpoint: `api/v1/donors/${ authUser.userId }`,
        onSuccess: (res) => {
          setDonor(new Donor(res.data as ApiDonor));
        },
      });
    }
  }, [ get, authUser ]);

  if (loading || !post) return <Spinner centered={ true } />;

  const isDonor = authUser?.userType === UserType.Donor;
  const isKycVerified = donor?.isKycCertifiedStrict;
  const isStdCertified = donor?.std.certified;
  const isExpired = !!post.expiresAt && new Date(post.expiresAt) < new Date();
  const isUrgent = !isExpired && post.daysLeft <= 20;
  const isNew = !!post.postedAt && Date.now() - new Date(post.postedAt).getTime() < 24 * 60 * 60 * 1000;
  const paragraphs = post.description ? post.description.split('\n').filter(Boolean) : [];

  const handleApplySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) {
      toast.error('コメントを入力してください。');
      return;
    }
    apiPost({
      endpoint: `api/v1/forum_posts/${ id }/forum_applications`,
      body: { comment },
      onSuccess: () => {
        setShowModal(false);
        setApplied(true);
        setApplicationsCount(c => c + 1);
        toast.success('応募しました！');
      },
      onError: () => {
        toast.error('応募に失敗しました。すでに応募済みか、本人確認または性病検査が必要な場合があります。');
      },
    });
  };

  const renderApplyButton = () => {
    if (isExpired) return null;
    if (!authUser) {
      return <Link to="/login" className="apply-btn">ログインして応募する</Link>;
    }
    if (!isDonor) return null;
    if (applied) {
      return <span className="applied-label">応募済みです</span>;
    }
    if (applicationsCount >= 20) {
      return <button type="button" className="apply-btn" disabled>応募上限に達しました</button>;
    }
    if (isKycVerified) {
      if (!isStdCertified) {
        return <Link to="/mypage/checkups/std" className="apply-btn">性病検査を登録して応募する</Link>;
      }
      return (
        <button type="button" className="apply-btn" onClick={ () => setShowModal(true) }>
          応募する
        </button>
      );
    }
    return <Link to="/mypage/kyc" className="apply-btn">本人確認をして応募する</Link>;
  };

  return (
    <div>
      <Header />
      <main className="sub">
        <section className="archive_forum">
          <div className="detail-shell">
            <nav className="detail-breadcrumb">
              <Link to="/forum">ドナー募集掲示板</Link>
              <span className="sep">›</span>
              <span className="current">募集詳細</span>
            </nav>

            <div className="detail-main">
              <header className="detail-head">
                <div className="head-info">
                  <div className="head-line">
                    <strong>{ post.doneeNameF }様</strong>
                    <span className="dot" />
                    <span>{ post.doneeAgeText }</span>
                    { isNew && <span className="new-pill">NEW</span> }
                  </div>
                  <div className="head-sub">
                    <span>投稿 <strong>{ post.postedAt ? formatDate(post.postedAt) : '—' }</strong></span>
                    <span className="dot" />
                    <span>
                      締め切り <strong>{ post.expiresAtText || '-' }</strong>
                      { post.expiresAt && (
                        isExpired
                          ? <em className="countdown expired">掲載終了</em>
                          : <em className={ `countdown${ isUrgent ? ' urgent' : '' }` }>あと{ post.daysLeft }日</em>
                      ) }
                    </span>
                  </div>
                </div>
                <div className="head-apps">
                  <span className="num">{ applicationsCount }<span className="cap">/20</span></span>
                  <span className="lbl">人 応募中</span>
                </div>
              </header>

              { paragraphs.length > 0 && (
                <section className="detail-block">
                  <h2 className="block-title">コメント</h2>
                  <div className="comment-full">
                    { paragraphs.map((p, i) => <p key={ i }>{ p }</p>) }
                  </div>
                </section>
              ) }

              <section className="detail-block">
                <h2 className="block-title">募集内容</h2>
                <dl className="spec-table">
                  <div className="spec-row">
                    <dt>エリア</dt>
                    <dd><span className="chip">{ post.areaText }</span></dd>
                  </div>
                  { post.bloodTypeChips.length > 0 && (
                    <div className="spec-row">
                      <dt>血液型</dt>
                      <dd>{ post.bloodTypeChips.map(v => <span key={ v } className="chip">{ v }</span>) }</dd>
                    </div>
                  ) }
                  { post.ageRange?.length && (
                    <div className="spec-row">
                      <dt>年齢</dt>
                      <dd>{ post.ageRange.map(k => <span key={ k } className="chip">{ AGE_RANGE_TEXTS[k] || k }</span>) }</dd>
                    </div>
                  ) }
                  { post.bodyFigure?.length && (
                    <div className="spec-row">
                      <dt>体型</dt>
                      <dd>{ post.bodyFigure.map(k => <span key={ k } className="chip">{ BODY_FIGURE_TEXTS[k] || k }</span>) }</dd>
                    </div>
                  ) }
                  { post.heightRange?.length && (
                    <div className="spec-row">
                      <dt>身長</dt>
                      <dd>{ post.heightRange.map(k => <span key={ k } className="chip">{ HEIGHT_RANGE_TEXTS[k] || k }</span>) }</dd>
                    </div>
                  ) }
                </dl>
              </section>

<div className="apply-zone">
                { renderApplyButton() }
              </div>

              <p className="report-line">
                <a href="https://docs.google.com/forms/d/e/1FAIpQLScGv-6GgDw8_xLOmch7yv7UGm7wgr64T7DYwhxMjZx3p8qHSg/viewform" target="_blank" rel="noreferrer">この投稿を通報する</a>
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      { showModal && (
        <div className="apply-modal-overlay" onClick={ () => setShowModal(false) }>
          <div className="apply-modal" onClick={ e => e.stopPropagation() }>
            <button type="button" className="apply-modal-close" onClick={ () => setShowModal(false) }>×</button>
            <h2 className="apply-modal-title">応募する</h2>
            <p className="apply-modal-sub">{ post.doneeNameF }様への応募</p>
            <form onSubmit={ handleApplySubmit }>
              <div className="apply-modal-field">
                <label htmlFor="apply-comment">一言コメント</label>
                <textarea
                  id="apply-comment"
                  value={ comment }
                  onChange={ e => setComment(e.target.value.slice(0, 300)) }
                  rows={ 4 }
                  placeholder="ドニーへの一言メッセージ"
                  required
                />
                <span className={ `char-count${ comment.length >= 300 ? ' at-limit' : '' }` }>
                  { comment.length } / 300
                </span>
              </div>
              <p className="apply-modal-notice">
                注意事項：<br/>
                ※ドニーからの連絡をお約束するものではありません。<br/>
                ※ドニーからメッセージを受け取った場合、原則として<span className="apply-modal-notice-highlight">24時間以内</span>に返信してください。
              </p>
              <button type="submit" className="apply-modal-btn" disabled={ loading }>
                { loading ? '送信中...' : '応募する' }
              </button>
            </form>
          </div>
        </div>
      ) }
    </div>
  );
};

export default ForumPostDetail;
